import { Component, OnInit, ChangeDetectorRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Observable, of, Subject } from 'rxjs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { AuthService } from '../../services/auth.service';
import { ClientCapabilityService } from '../../services/client-capability.service';
import { NotificationService } from '../../services/notification.service';
import { DocumentModelService } from '../../services/document-model.service';
import { CanComponentDeactivate } from '../../guards/can-deactivate.guard';
import { TruthManagement as TruthManagementService } from '../../services/truth-management';
import { PageHeaderComponent } from '../page-header/page-header.component';

interface EditField {
  fieldName: string;
  fieldType: string;
  feature: string;
  prompt: string;
  additionalPrompt: string;
  fieldSchema: string;
  computeSchema: string;
  externalKey: string;
  modifiedAt: string;
  isEditing: boolean;
  isNew: boolean;
  originalFieldName: string;
  originalSchema: any;
  originalFeature: string;
  jsonFile?: File | null;
  parsedJsonContent?: any;
}

interface PreservedEditingRow {
  values: Partial<EditField>;
  snapshot?: Partial<EditField>;
}

interface PreservedEditingState {
  existingRows: Map<string, PreservedEditingRow>;
  newRows: PreservedEditingRow[];
}

interface PromptTuningGenerateDraft {
  documentModel: string;
  createdAt: number;
  returnUrl: string;
  autoCreate?: boolean;
  fields?: PromptTuningGenerateDraftField[];
  fieldName: string;
  fieldType: string;
  feature: string;
  prompt: string;
  additionalPrompt: string;
  fieldSchema: string;
  computeSchema: string;
  externalKey: string;
}

interface PromptTuningGenerateDraftField {
  fieldName: string;
  fieldType: string;
  feature: string;
  prompt: string;
  additionalPrompt: string;
  fieldSchema: string;
  computeSchema: string;
  externalKey: string;
}

@Component({
  selector: 'app-edit-document-model',
  standalone: true,
  imports: [CommonModule, FormsModule, PageHeaderComponent],
  templateUrl: './edit-document-model.html',
  styleUrl: './edit-document-model.scss',
})
export class EditDocumentModel implements OnInit, CanComponentDeactivate {
  documentModel: string = '';
  fields: EditField[] = [];
  isLoading: boolean = true;
  isSubmitting: boolean = false;

  sortColumn: 'fieldName' | 'fieldType' | 'feature' | 'modifiedAt' | null = null;
  sortDirection: 'asc' | 'desc' = 'asc';
  sortActive: boolean = false;
  fieldSearchQuery: string = '';

  private get fieldSearchTerm(): string {
    return (this.fieldSearchQuery || '').trim().toLowerCase();
  }

  get hasNoSearchResults(): boolean {
    if (!this.fieldSearchTerm || this.fields.length === 0) return false;
    const q = this.fieldSearchTerm;
    return !this.fields.some(f => f.isNew || f.fieldName.toLowerCase().includes(q));
  }

  shouldHideFieldRow(field: EditField): boolean {
    if (field.isNew || !this.fieldSearchTerm) {
      return false;
    }
    return !field.fieldName.toLowerCase().includes(this.fieldSearchTerm);
  }

  simpleFeatureOptions: string[] = ['Normal', 'Seal', 'Signature', 'Date'];
  arrayFeatureOptions: string[] = ['Normal', 'Signature', 'Date'];

  getFeatureOptions(fieldType: string): string[] {
    if (fieldType === 'Array') return this.arrayFeatureOptions;
    if (fieldType === 'Compute') return [];
    return this.simpleFeatureOptions;
  }
  fieldTypeOptions: string[] = ['Simple', 'Array', 'Complex', 'Compute'];

  promptExpanded: { [key: number]: boolean } = {};
  additionalPromptExpanded: { [key: number]: boolean } = {};
  fieldSchemaExpanded: { [key: number]: boolean } = {};
  computeSchemaExpanded: { [key: number]: boolean } = {};
  externalKeyExpanded: { [key: number]: boolean } = {};

  jsonSchemaErrors: { [key: string]: string } = {};
  jsonPreviewOpen: { [key: number]: boolean } = {};
  jsonPreviewPos: { [key: number]: { top: number; left: number } } = {};

  // Hover tooltip state for read-only schema cells
  hoveredSchemaIndex: number = -1;
  hoveredComputeSchemaIndex: number = -1;
  schemaTooltipPos = { top: 0, left: 0, up: false };
  computeSchemaTooltipPos = { top: 0, left: 0, up: false };

  showConfirmModal: boolean = false;
  confirmAction: string = '';
  confirmMessage: string = '';
  confirmIndex: number = -1;
  changeReason: string = '';
  hasUnsavedChanges: boolean = false;
  showUnsavedModal: boolean = false;
  private unsavedModalDecision$ = new Subject<boolean>();

  // Cell-level popup editor state
  editPopupIndex: number = -1;
  editPopupColumn: string = '';
  private _popupOpenValue: any = undefined; // value when popup opened — used by Cancel to revert

  // Inline-select state (Type / Feature dropdowns rendered inside the table cell)
  inlineEditIndex: number = -1;
  inlineEditColumn: string = '';
  private _inlineOpenValue: any = undefined; // value when inline opened — blur reverts to this

  // JSON upload loading state
  isJsonUploading: boolean = false;
  private _fileSelected = false;
  private unsavedModalTriggeredByGuard = false;
  private readonly trainingGenerateDraftStorageKey = 'promptTuningGenerateDraft';
  private readonly trainingGenerateDraftTtlMs = 10 * 60 * 1000;
  private promptTuningReturnUrl: string | null = null;

  /**
   * Check if there are actual unsaved changes (fields in editing with modifications)
   */
  get hasActualChanges(): boolean {
    return this.fields.some((f, i) => f.isEditing && this.hasFieldChanged(i));
  }

  get backButtonTooltip(): string {
    return this.promptTuningReturnUrl ? 'Back to Prompt tuning' : 'Back to Admin';
  }

  constructor(
    public router: Router,
    private route: ActivatedRoute,
    public authService: AuthService,
    private notificationService: NotificationService,
    private documentModelService: DocumentModelService,
    private truthManagementService: TruthManagementService,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private clientCapability: ClientCapabilityService,
  ) {}

  ngOnInit(): void {
    // Check for field search query parameter from navigation
    this.route.queryParams.subscribe(params => {
      if (params['search']) {
        this.fieldSearchQuery = params['search'];
      }
    });

    this.route.params.subscribe(params => {
      this.documentModel = params['documentModel'] || '';
      if (this.documentModel) {
        this.loadFields();
      }
    });
  }

  formatDocumentModelForDisplay(name: string | null | undefined): string {
    if (!name) {
      return '-';
    }

    return name
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .split(' ')
      .filter(part => part.length > 0)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }

  /** Only admin and superuser roles may add, edit or delete fields */
  get canEdit(): boolean {
    return this.authService.isAdmin;
  }

  get isAuthenticated(): boolean {
    return !!sessionStorage.getItem('userEmail');
  }

  /**
   * CanDeactivate guard interface implementation
   */
  canDeactivate(): Observable<boolean> | boolean {
    if (!this.hasActualChanges) {
      return true;
    }
    this.unsavedModalTriggeredByGuard = true;
    this.showUnsavedModal = true;
    return this.unsavedModalDecision$.asObservable();
  }

  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasActualChanges) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  /**
   * Navigate back to admin settings — guard will intercept if unsaved changes
   */
  goBack(): void {
    if (this.promptTuningReturnUrl) {
      this.router.navigateByUrl(this.promptTuningReturnUrl);
      return;
    }

    this.router.navigate(['/admin-settings']);
  }

  /** Unsaved modal: Discard changes and leave */
  unsavedDontSave(): void {
    this.showUnsavedModal = false;
    this.hasUnsavedChanges = false;
    // Reset editing state so guard won't re-trigger
    this.fields.forEach(f => { f.isEditing = false; });
    this.originalFieldSnapshots = {};
    this.unsavedModalDecision$.next(true);
  }

  /** Unsaved modal: Stay on page */
  unsavedStay(): void {
    this.showUnsavedModal = false;
    this.unsavedModalDecision$.next(false);
  }

  /** Unsaved modal: Save all editing fields and leave */
  unsavedSaveAndLeave(): void {
    this.showUnsavedModal = false;
    // Find all fields that are editing and have actual changes
    const editingIndices = this.fields
      .map((f, i) => (f.isEditing && this.hasFieldChanged(i)) ? i : -1)
      .filter(i => i !== -1);

    if (editingIndices.length === 0) {
      this.hasUnsavedChanges = false;
      this.unsavedModalDecision$.next(true);
      return;
    }

    // Save all changed fields directly via API (bypass confirmation modal)
    let completed = 0;
    const total = editingIndices.length;
    let hasError = false;

    editingIndices.forEach(idx => {
      const field = this.fields[idx];
      const schemaPayload = this.buildSchemaPayloadForField(field);

      if (field.isNew) {
        this.documentModelService.createField(this.documentModel, field.fieldName.trim(), {
          field_name: field.fieldName.trim(),
          document_model: this.documentModel,
          field_schema: schemaPayload.fieldSchema,
          computed_schema: schemaPayload.computedSchema,
          is_internal: false
        }).subscribe({
          next: () => {
            completed++;
            if (completed === total && !hasError) {
              this.hasUnsavedChanges = false;
              this.notificationService.showSuccess('All changes saved successfully');
              this.unsavedModalDecision$.next(true);
            }
          },
          error: (error) => {
            hasError = true;
            const msg = error.error?.detail || error.error?.message || 'Unknown error';
            this.notificationService.showError(`Failed to save field "${field.fieldName}": ${msg}`);
          }
        });
      } else {
        const payload: any = {
          change_reason: 'Auto-saved on navigation',
          field_schema: schemaPayload.fieldSchema,
          computed_schema: schemaPayload.computedSchema
        };

        this.documentModelService.updateField(this.documentModel, field.originalFieldName || field.fieldName.trim(), payload).subscribe({
          next: () => {
            completed++;
            if (completed === total && !hasError) {
              this.hasUnsavedChanges = false;
              this.notificationService.showSuccess('All changes saved successfully');
              this.unsavedModalDecision$.next(true);
            }
          },
          error: (error) => {
            hasError = true;
            const msg = error.error?.detail || error.error?.message || 'Unknown error';
            this.notificationService.showError(`Failed to save field "${field.fieldName}": ${msg}`);
          }
        });
      }
    });
  }

  private getFieldMatchKey(field: EditField): string {
    return (field.originalFieldName || field.fieldName || '').trim().toLowerCase();
  }

  private captureEditableFieldState(field: EditField): Partial<EditField> {
    return {
      fieldName: field.fieldName,
      fieldType: field.fieldType,
      feature: field.feature,
      prompt: field.prompt,
      additionalPrompt: field.additionalPrompt,
      fieldSchema: field.fieldSchema,
      computeSchema: field.computeSchema,
      externalKey: field.externalKey,
      modifiedAt: field.modifiedAt,
      isEditing: field.isEditing,
      isNew: field.isNew,
      originalFieldName: field.originalFieldName,
      originalSchema: field.originalSchema ? JSON.parse(JSON.stringify(field.originalSchema)) : field.originalSchema,
      originalFeature: field.originalFeature,
      jsonFile: field.jsonFile ?? null,
      parsedJsonContent: field.parsedJsonContent
    };
  }

  private captureEditingState(excludedFieldName: string = ''): PreservedEditingState {
    const excludedKey = excludedFieldName.trim().toLowerCase();
    const state: PreservedEditingState = {
      existingRows: new Map<string, PreservedEditingRow>(),
      newRows: []
    };

    this.fields.forEach((field, index) => {
      if (!field.isEditing) return;

      const rowState: PreservedEditingRow = {
        values: this.captureEditableFieldState(field),
        snapshot: this.originalFieldSnapshots[index] ? { ...this.originalFieldSnapshots[index] } : undefined
      };

      if (field.isNew) {
        state.newRows.push(rowState);
        return;
      }

      const key = this.getFieldMatchKey(field);
      if (!key || key === excludedKey) return;
      state.existingRows.set(key, rowState);
    });

    return state;
  }

  private restoreEditingState(state?: PreservedEditingState): void {
    if (!state) return;

    this.originalFieldSnapshots = {};

    for (const [key, rowState] of state.existingRows.entries()) {
      const idx = this.fields.findIndex(f => this.getFieldMatchKey(f) === key);
      if (idx < 0) continue;

      Object.assign(this.fields[idx], rowState.values);
      this.fields[idx].isEditing = true;
      this.fields[idx].isNew = false;

      if (rowState.snapshot) {
        this.originalFieldSnapshots[idx] = { ...rowState.snapshot };
      }
    }

    for (const rowState of state.newRows) {
      const values = rowState.values;
      const restoredNewRow: EditField = {
        fieldName: values.fieldName || '',
        fieldType: values.fieldType || 'Simple',
        feature: values.feature || '',
        prompt: values.prompt || '',
        additionalPrompt: values.additionalPrompt || '',
        fieldSchema: values.fieldSchema || '',
        computeSchema: values.computeSchema || '',
        externalKey: values.externalKey || '',
        modifiedAt: values.modifiedAt || '',
        isEditing: true,
        isNew: true,
        originalFieldName: values.originalFieldName || '',
        originalSchema: values.originalSchema ?? null,
        originalFeature: values.originalFeature || '',
        jsonFile: values.jsonFile ?? null,
        parsedJsonContent: values.parsedJsonContent
      };

      this.fields.push(restoredNewRow);
      const newIndex = this.fields.length - 1;
      if (rowState.snapshot) {
        this.originalFieldSnapshots[newIndex] = { ...rowState.snapshot };
      }
    }

    this.hasUnsavedChanges = this.fields.some(f => f.isEditing);
  }

  private loadFieldsPreservingEditState(excludedFieldName: string = ''): void {
    const state = this.captureEditingState(excludedFieldName);
    this.loadFields(state);
  }

  private loadFields(stateToRestore?: PreservedEditingState): void {
    this.isLoading = true;

    this.documentModelService.getFieldSchemas(this.documentModel).subscribe({
      next: (response) => {
        this.fields = this.mapResponseToFields(response);
        if (!this.sortColumn) {
          this.sortColumn = 'modifiedAt';
          this.sortDirection = 'desc';
        }
        this.applyCurrentSort();
        this.restoreEditingState(stateToRestore);
        this.applyCurrentSort();
        if (!stateToRestore) {
          this.applyPromptTuningGenerateDraft();
        }
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.isLoading = false;
        const msg = error.error?.detail || error.error?.message || (typeof error.error === 'string' ? error.error : error.message || 'Unknown error');
        if (!msg.toLowerCase().includes('no active field schemas found')) {
          this.notificationService.showError(`Failed to load fields: ${msg}`);
        }
        this.cdr.detectChanges();
      }
    });
  }

  private mapResponseToFields(response: any): EditField[] {
    const fields: EditField[] = [];

    if (response && response.fields && Array.isArray(response.fields)) {
      for (const f of response.fields) {
        let fieldType = 'Simple';
        let feature = '';
        let prompt = '';
        let additionalPrompt = '';
        let fieldSchema = '';
        let computeSchema = '';
        let externalKey = '';
        const schema = (f.field_schema && typeof f.field_schema === 'object') ? f.field_schema : null;
        const schemaForAccess: any = schema || {};

        // Detect compute schema payload and classify type.
        const hasComputedSchema = f.computed_schema && Object.keys(f.computed_schema).length > 0;

        // Key-based classification: detect Complex if schema has keys beyond the known Simple/Array set
        const allowedTopKeys = new Set(['description', 'type', 'ld_feature', 'items', 'external_key']);
        const allowedItemsKeys = new Set(['description', 'ld_feature', 'external_key']);
        const schemaKeys = schema ? Object.keys(schema) : [];
        const hasExtraKeys = schemaKeys.some(k => !allowedTopKeys.has(k));
        const hasExtraItemsKeys = schema && schema.items && typeof schema.items === 'object'
          ? Object.keys(schema.items).some(k => !allowedItemsKeys.has(k))
          : false;

        if (hasComputedSchema && (!schema || Object.keys(schema).length === 0)) {
          fieldType = 'Compute';
          computeSchema = JSON.stringify(f.computed_schema, null, 2);
        } else if (hasComputedSchema || hasExtraKeys || hasExtraItemsKeys) {
          fieldType = 'Complex';
          if (schema && Object.keys(schema).length > 0) {
            fieldSchema = JSON.stringify(schema, null, 2);
          }
          if (hasComputedSchema) {
            computeSchema = JSON.stringify(f.computed_schema, null, 2);
          }
        } else if (schemaForAccess.type === 'array') {
          fieldType = 'Array';
          prompt = schemaForAccess.items?.description || '';
          additionalPrompt = schemaForAccess.description || '';
          externalKey = schemaForAccess.items?.external_key || '';
        } else {
          fieldType = 'Simple';
          prompt = schemaForAccess.description || '';
          externalKey = schemaForAccess.external_key || '';
        }

        // Check both top-level feature AND field_schema.ld_feature (not for Complex)
        if (fieldType !== 'Complex' && fieldType !== 'Compute') {
          const resolvedFeature = f.feature ? f.feature : (schemaForAccess.ld_feature || schemaForAccess.items?.ld_feature || '');
          if (resolvedFeature) {
            feature = resolvedFeature.charAt(0).toUpperCase() + resolvedFeature.slice(1).toLowerCase();
          }
        }

        // Append 'Z' to modified_at if no timezone info so JS Date treats it as UTC
        let modifiedAt = f.modified_at || '';
        if (modifiedAt && !modifiedAt.endsWith('Z') && !modifiedAt.includes('+')) {
          modifiedAt += 'Z';
        }

        fields.push({
          fieldName: f.field_name || '',
          fieldType,
          feature,
          prompt,
          additionalPrompt,
          fieldSchema,
          computeSchema,
          externalKey,
          modifiedAt,
          isEditing: false,
          isNew: false,
          originalFieldName: f.field_name || '',
          originalSchema: f.field_schema ? JSON.parse(JSON.stringify(f.field_schema)) : {},
          originalFeature: f.feature || ''
        });
      }
    }

    // Sort fields alphabetically by name (natural sort for numeric suffixes)
    fields.sort((a, b) => a.fieldName.localeCompare(b.fieldName, undefined, { numeric: true, sensitivity: 'base' }));
    return fields;
  }

  isFieldFrozen(field: EditField, column: string): boolean {
    switch (field.fieldType) {
      case 'Simple':
        return ['additionalPrompt', 'fieldSchema', 'computeSchema'].includes(column);
      case 'Array':
        return ['fieldSchema', 'computeSchema'].includes(column);
      case 'Complex':
        return ['prompt', 'additionalPrompt', 'feature', 'externalKey'].includes(column);
      case 'Compute':
        return ['prompt', 'additionalPrompt', 'feature', 'fieldSchema', 'externalKey'].includes(column);
      default:
        return false;
    }
  }

  /** Returns true if column is required (must be filled) for the given field type */
  isRequiredColumn(fieldType: string, column: string): boolean {
    switch (fieldType) {
      case 'Simple':  return ['feature', 'prompt'].includes(column);
      case 'Array':   return ['feature', 'prompt', 'additionalPrompt'].includes(column);
      case 'Complex': return ['fieldSchema'].includes(column);
      case 'Compute': return ['computeSchema'].includes(column);
      default:        return false;
    }
  }

  /** True when an editing row's cell is required but currently empty */
  isCellRequiredEmpty(field: EditField, column: string): boolean {
    if (!field.isEditing) return false;
    if (!this.isRequiredColumn(field.fieldType, column)) return false;

    if (field.fieldType === 'Complex' && column === 'fieldSchema') {
      return !field.fieldSchema?.trim();
    }

    if (field.fieldType === 'Compute' && column === 'computeSchema') {
      return !field.computeSchema?.trim();
    }

    const val = (field as any)[column];
    return !val || !String(val).trim();
  }

  // Store original field snapshots for change detection
  private originalFieldSnapshots: { [key: number]: Partial<EditField> } = {};

  editField(index: number): void {
    if (!this.canEdit) return;
    const field = this.fields[index];
    field.isEditing = true;
    field.originalFieldName = field.fieldName;
    // Snapshot for change detection (Issue 7: Done button)
    this.originalFieldSnapshots[index] = {
      fieldName: field.fieldName,
      fieldType: field.fieldType,
      feature: field.feature,
      prompt: field.prompt,
      additionalPrompt: field.additionalPrompt,
      fieldSchema: field.fieldSchema,
      computeSchema: field.computeSchema,
      externalKey: field.externalKey,
    };
  }

  /**
   * Check if a field has been modified since entering edit mode
   */
  hasFieldChanged(index: number): boolean {
    const field = this.fields[index];
    const original = this.originalFieldSnapshots[index];
    if (!original) return true; // New fields always have changes
    return (
      field.fieldName !== original.fieldName ||
      field.fieldType !== original.fieldType ||
      field.feature !== original.feature ||
      field.prompt !== original.prompt ||
      field.additionalPrompt !== original.additionalPrompt ||
      field.fieldSchema !== original.fieldSchema ||
      field.computeSchema !== original.computeSchema ||
      field.externalKey !== original.externalKey
    );
  }

  /**
   * Check if mandatory fields are filled for a given row based on its type
   */
  isFieldValid(index: number): boolean {
    const field = this.fields[index];
    if (!field.fieldName || !field.fieldName.trim()) return false;

    switch (field.fieldType) {
      case 'Simple':
        return !!(field.feature && field.feature.trim() && field.prompt && field.prompt.trim());
      case 'Array':
        return !!(field.feature && field.feature.trim() && field.prompt && field.prompt.trim() && field.additionalPrompt && field.additionalPrompt.trim());
      case 'Complex':
        if (this.hasJsonError(index, 'fieldSchema') || this.hasJsonError(index, 'computeSchema')) return false;
        return !!(field.fieldSchema?.trim());
      case 'Compute':
        if (this.hasJsonError(index, 'computeSchema')) return false;
        return !!(field.computeSchema?.trim());
      default:
        return false;
    }
  }

  private buildSchemaPayloadForField(field: EditField): { fieldSchema: any; computedSchema: any } {
    const isComplex = field.fieldType === 'Complex';
    const isCompute = field.fieldType === 'Compute';

    let fieldSchema: any = this.buildSingleFieldSchema(field);
    if ((isComplex && !field.fieldSchema?.trim()) || isCompute) {
      fieldSchema = null;
    }

    let computedSchema: any = null;
    if ((isComplex || isCompute) && field.computeSchema?.trim()) {
      try {
        computedSchema = JSON.parse(field.computeSchema.trim());
      } catch {
        computedSchema = null;
      }
    }

    return { fieldSchema, computedSchema };
  }

  cancelEdit(index: number): void {
    const field = this.fields[index];
    const snapshot = this.originalFieldSnapshots[index];
    delete this.originalFieldSnapshots[index];
    if (field.isNew) {
      this.fields.splice(index, 1);
      // Re-key snapshots for shifted indices
      const updated: { [key: number]: Partial<EditField> } = {};
      for (const key of Object.keys(this.originalFieldSnapshots)) {
        const k = Number(key);
        updated[k > index ? k - 1 : k] = this.originalFieldSnapshots[k];
      }
      this.originalFieldSnapshots = updated;
    } else {
      // Restore only this row from its snapshot (does not affect other rows)
      if (snapshot) {
        field.fieldName = snapshot.fieldName || field.fieldName;
        field.fieldType = snapshot.fieldType || field.fieldType;
        field.feature = snapshot.feature ?? '';
        field.prompt = snapshot.prompt ?? '';
        field.additionalPrompt = snapshot.additionalPrompt ?? '';
        field.fieldSchema = snapshot.fieldSchema ?? '';
        field.computeSchema = snapshot.computeSchema ?? '';
        field.externalKey = snapshot.externalKey ?? '';
      }
      field.isEditing = false;
    }
    // Check if any fields are still in editing mode
    this.hasUnsavedChanges = this.fields.some(f => f.isEditing);
  }

  saveField(index: number): void {
    if (this.isSubmitting) return;
    const field = this.fields[index];
    if (!field.fieldName.trim()) {
      this.notificationService.showError('Field Name is required');
      return;
    }
    const nameToCheck = field.fieldName.trim().toLowerCase();
    const isDuplicate = this.fields.some((f, idx) => idx !== index && f.fieldName.trim().toLowerCase() === nameToCheck);
    if (isDuplicate) {
      this.notificationService.showError(`A field named "${field.fieldName.trim()}" already exists`);
      return;
    }

    if (field.isNew) {
      this.confirmAction = 'create';
      this.confirmMessage = `Are you sure you want to add field "${field.fieldName}"?`;
      this.confirmIndex = index;
      this.showConfirmModal = true;
    } else {
      this.confirmAction = 'update';
      this.confirmMessage = `Provide a reason for updating "${field.fieldName}"`;
      this.confirmIndex = index;
      this.changeReason = '';
      this.showConfirmModal = true;
    }
  }

  private executeCreateField(index: number): void {
    const field = this.fields[index];
    const schemaPayload = this.buildSchemaPayloadForField(field);
    this.isSubmitting = true;
    this.cdr.detectChanges();

    this.documentModelService.createField(this.documentModel, field.fieldName.trim(), {
      field_name: field.fieldName.trim(),
      document_model: this.documentModel,
      field_schema: schemaPayload.fieldSchema,
      computed_schema: schemaPayload.computedSchema,
      is_internal: false
    }).subscribe({
      next: () => {
        this.editPopupIndex = -1;
        this.editPopupColumn = '';
        field.isEditing = false;
        field.isNew = false;
        field.originalFieldName = field.fieldName;
        delete this.originalFieldSnapshots[index];
        this.isSubmitting = false;
        this.hasUnsavedChanges = this.fields.some(f => f.isEditing);
        this.notificationService.showSuccess(`Field "${field.fieldName}" created successfully`);
        // Invalidate cache so Truth Management fetches fresh schema on next visit.
        this.truthManagementService.invalidateDocumentModelCache(this.documentModel);
        this.cdr.detectChanges();
        // Reload from server to fetch backend-assigned metadata (e.g., modified_at).
        this.loadFieldsPreservingEditState(field.originalFieldName || field.fieldName.trim());
      },
      error: (error) => {
        this.isSubmitting = false;
        const msg = error.error?.detail || error.error?.message || (typeof error.error === 'string' ? error.error : error.message || 'Unknown error');
        this.notificationService.showError(`Failed to create field: ${msg}`);
        this.cdr.detectChanges();
      }
    });
  }

  private executeUpdateField(index: number, reason: string): void {
    const field = this.fields[index];
    const schemaPayload = this.buildSchemaPayloadForField(field);
    this.isSubmitting = true;
    this.cdr.detectChanges();

    const payload: any = {
      change_reason: reason,
      field_schema: schemaPayload.fieldSchema
    };
    payload.computed_schema = schemaPayload.computedSchema;

    this.documentModelService.updateField(this.documentModel, field.originalFieldName || field.fieldName.trim(), payload).subscribe({
      next: () => {
        this.editPopupIndex = -1;
        this.editPopupColumn = '';
        this.isSubmitting = false;
        field.isEditing = false;
        delete this.originalFieldSnapshots[index];
        this.hasUnsavedChanges = this.fields.some(f => f.isEditing);
        this.notificationService.showSuccess(`Field "${field.fieldName}" updated successfully`);
        // Invalidate cache so Truth Management fetches fresh schema on next visit.
        this.truthManagementService.invalidateDocumentModelCache(this.documentModel);
        // Reload from server to confirm persistence and refresh originalSchema
        this.loadFieldsPreservingEditState(field.originalFieldName || field.fieldName.trim());
      },
      error: (error) => {
        this.isSubmitting = false;
        const msg = error.error?.detail || error.error?.message || (typeof error.error === 'string' ? error.error : error.message || 'Unknown error');
        this.notificationService.showError(`Failed to update field: ${msg}`);
        this.cdr.detectChanges();
      }
    });
  }

  private buildSingleFieldSchema(field: EditField): any {
    const featureLower = (field.feature || '').toLowerCase();
    const hasFeature = featureLower && featureLower !== 'normal';
    const hasExternalKey = !!(field.externalKey && field.externalKey.trim());

    // For existing fields, deep-clone the original schema and patch only changed properties
    // This preserves all backend properties that the UI doesn't display
    if (!field.isNew && field.originalSchema) {
      const schema = JSON.parse(JSON.stringify(field.originalSchema));

      switch (field.fieldType) {
        case 'Simple': {
          const nextSchema: any = { description: field.prompt || '' };
          if (hasFeature) nextSchema.ld_feature = featureLower;
          if (hasExternalKey) nextSchema.external_key = field.externalKey.trim();
          return nextSchema;
        }
        case 'Array': {
          const items: any = { description: field.prompt || '' };
          if (hasExternalKey) items.external_key = field.externalKey.trim();
          const nextSchema: any = {
            type: 'array',
            items,
            description: field.additionalPrompt || ''
          };
          if (hasFeature) nextSchema.ld_feature = featureLower;
          return nextSchema;
        }
        case 'Complex': {
          const raw = field.fieldSchema?.trim() || '{}';
          try { return JSON.parse(raw); } catch {
            try { return JSON.parse('{' + raw + '}'); } catch { return schema; }
          }
        }
        case 'Compute':
          return null;
      }

      return schema;
    }

    // For new fields, build from scratch
    switch (field.fieldType) {
      case 'Simple': {
        const schema: any = { description: field.prompt || '' };
        if (hasFeature) schema.ld_feature = featureLower;
        if (hasExternalKey) schema.external_key = field.externalKey.trim();
        return schema;
      }
      case 'Array': {
        const items: any = { description: field.prompt || '' };
        if (hasExternalKey) items.external_key = field.externalKey.trim();
        const schema: any = {
          type: 'array',
          items,
          description: field.additionalPrompt || ''
        };
        if (hasFeature) schema.ld_feature = featureLower;
        return schema;
      }
      case 'Complex': {
        const raw = field.fieldSchema?.trim() || '{}';
        try {
          return JSON.parse(raw);
        } catch {
          try {
            return JSON.parse('{' + raw + '}');
          } catch {
            return {};
          }
        }
      }
      case 'Compute':
        return null;
      default:
        return {};
    }
  }

  private applyCurrentSort(): void {
    if (!this.sortColumn) return;
    const col = this.sortColumn;
    const dir = this.sortDirection;

    // Preserve snapshots keyed by field object reference before indices change
    const snapshotsByField = new Map<EditField, Partial<EditField>>();
    for (const key of Object.keys(this.originalFieldSnapshots)) {
      const field = this.fields[Number(key)];
      if (field) snapshotsByField.set(field, this.originalFieldSnapshots[Number(key)]);
    }

    this.fields = [...this.fields].sort((a, b) => {
      if (a.isNew && !b.isNew) return -1;
      if (!a.isNew && b.isNew) return 1;
      let cmp: number;
      if (col === 'modifiedAt') {
        const aTime = a.modifiedAt ? new Date(a.modifiedAt).getTime() : 0;
        const bTime = b.modifiedAt ? new Date(b.modifiedAt).getTime() : 0;
        cmp = aTime - bTime;
      } else {
        const aVal = (a[col] ?? '').toLowerCase();
        const bVal = (b[col] ?? '').toLowerCase();
        cmp = aVal.localeCompare(bVal);
      }
      return dir === 'asc' ? cmp : -cmp;
    });

    // Re-key snapshots to new index positions
    this.originalFieldSnapshots = {};
    this.fields.forEach((field, newIdx) => {
      const snapshot = snapshotsByField.get(field);
      if (snapshot) this.originalFieldSnapshots[newIdx] = snapshot;
    });
  }

  sortBy(column: 'fieldName' | 'fieldType' | 'feature' | 'modifiedAt'): void {
    if (!this.sortActive) {
      this.sortActive = true;
      this.sortColumn = column;
      this.sortDirection = 'asc';
    } else if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.applyCurrentSort();
    this.cdr.detectChanges();
  }

  getSortIcon(column: string): SafeHtml {
    if (!this.sortActive || this.sortColumn !== column) {
      return this.sanitizer.bypassSecurityTrustHtml('<div style="display:flex;flex-direction:column;align-items:center;line-height:0.8;font-family:Arial,sans-serif"><span>&#9650;</span><span>&#9660;</span></div>');
    }
    return this.sanitizer.bypassSecurityTrustHtml(this.sortDirection === 'asc' ? '&#9650;' : '&#9660;');
  }

  addField(): void {
    if (!this.canEdit) return;
    this.fields.push({
      fieldName: '',
      fieldType: 'Simple',
      feature: '',
      prompt: '',
      additionalPrompt: '',
      fieldSchema: '',
      computeSchema: '',
      externalKey: '',
      modifiedAt: '',
      isEditing: true,
      isNew: true,
      originalFieldName: '',
      originalSchema: null,
      originalFeature: ''
    });
    // Snapshot immediately so hasFieldChanged works
    const newIndex = this.fields.length - 1;
    this.originalFieldSnapshots[newIndex] = {
      fieldName: '',
      fieldType: 'Simple',
      feature: '',
      prompt: '',
      additionalPrompt: '',
      fieldSchema: '',
      computeSchema: '',
      externalKey: '',
    };
    this.applyCurrentSort();
    this.hasUnsavedChanges = true;
    this.cdr.detectChanges();
    // Scroll new row into view so user can see it and click cells to fill
    setTimeout(() => {
      const newRows = document.querySelectorAll('tr.new-row');
      if (newRows.length > 0) {
        newRows[newRows.length - 1].scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 0);
  }

  private applyPromptTuningGenerateDraft(): void {
    const queryReturnUrl = this.resolvePromptTuningReturnUrlFromQuery();
    const draft = this.consumePromptTuningGenerateDraft();
    if (!draft) {
      this.promptTuningReturnUrl = queryReturnUrl;
      return;
    }

    const draftReturnUrl = draft.returnUrl && draft.returnUrl.startsWith('/prompt-tuning/')
      ? draft.returnUrl
      : null;
    this.promptTuningReturnUrl = queryReturnUrl || draftReturnUrl;

    if (!this.canEdit) {
      return;
    }

    const draftFields = this.getPromptTuningDraftFields(draft);
    if (draftFields.length === 0) {
      return;
    }

    if (draft.autoCreate) {
      this.autoCreatePromptTuningDraftFields(draftFields);
      return;
    }

    const newRows: EditField[] = draftFields.map(draftField => this.buildEditRowFromPromptDraft(draftField));

    const shiftedSnapshots: { [key: number]: Partial<EditField> } = {};
    for (const key of Object.keys(this.originalFieldSnapshots)) {
      shiftedSnapshots[Number(key) + newRows.length] = this.originalFieldSnapshots[Number(key)];
    }
    this.originalFieldSnapshots = shiftedSnapshots;

    this.fields = [...newRows, ...this.fields];
    newRows.forEach((_, index) => {
      this.originalFieldSnapshots[index] = {
        fieldName: '',
        fieldType: 'Simple',
        feature: '',
        prompt: '',
        additionalPrompt: '',
        fieldSchema: '',
        computeSchema: '',
        externalKey: '',
      };
    });

    this.hasUnsavedChanges = true;
  }

  private resolvePromptTuningReturnUrlFromQuery(): string | null {
    const from = (this.route.snapshot.queryParamMap.get('from') || '').trim().toLowerCase();
    if (from !== 'prompt-tuning') {
      return null;
    }

    const returnUrl = (this.route.snapshot.queryParamMap.get('returnUrl') || '').trim();
    if (!returnUrl.startsWith('/prompt-tuning/')) {
      return null;
    }

    return returnUrl;
  }

  private autoCreatePromptTuningDraftFields(draftFields: PromptTuningGenerateDraftField[]): void {
    const existingFieldNames = new Set(
      this.fields
        .map(field => field.fieldName.trim().toLowerCase())
        .filter(name => !!name)
    );

    let createdCount = 0;
    let skippedCount = 0;
    let failedCount = 0;

    this.isSubmitting = true;
    this.cdr.detectChanges();

    const processNext = (index: number) => {
      if (index >= draftFields.length) {
        this.isSubmitting = false;
        this.hasUnsavedChanges = false;
        this.truthManagementService.invalidateDocumentModelCache(this.documentModel);

        if (createdCount === 0 && failedCount > 0) {
          this.notificationService.showError(`Generate failed for all fields (${failedCount}). Please try again.`);
        }

        this.loadFields();
        return;
      }

      const draftField = draftFields[index];
      const trimmedFieldName = String(draftField.fieldName || '').trim();
      const fieldNameKey = trimmedFieldName.toLowerCase();

      if (!trimmedFieldName) {
        failedCount += 1;
        processNext(index + 1);
        return;
      }

      if (existingFieldNames.has(fieldNameKey)) {
        skippedCount += 1;
        processNext(index + 1);
        return;
      }

      const mappedField = this.buildEditRowFromPromptDraft(draftField);
      const schemaPayload = this.buildSchemaPayloadForField(mappedField);

      this.documentModelService.createField(this.documentModel, trimmedFieldName, {
        field_name: trimmedFieldName,
        document_model: this.documentModel,
        field_schema: schemaPayload.fieldSchema,
        computed_schema: schemaPayload.computedSchema,
        is_internal: false
      }).subscribe({
        next: () => {
          createdCount += 1;
          existingFieldNames.add(fieldNameKey);
          this.notificationService.showSuccess(`Field "${trimmedFieldName}" created successfully`);
          processNext(index + 1);
        },
        error: (error) => {
          const msg = String(
            error?.error?.detail ||
            error?.error?.message ||
            (typeof error?.error === 'string' ? error.error : error?.message || '')
          ).toLowerCase();

          const isAlreadyExists = error?.status === 409 || msg.includes('already exists') || msg.includes('duplicate');
          if (isAlreadyExists) {
            skippedCount += 1;
            existingFieldNames.add(fieldNameKey);
          } else {
            failedCount += 1;
          }

          processNext(index + 1);
        }
      });
    };

    processNext(0);
  }

  private getPromptTuningDraftFields(draft: PromptTuningGenerateDraft): PromptTuningGenerateDraftField[] {
    if (Array.isArray(draft.fields) && draft.fields.length > 0) {
      return draft.fields;
    }

    return [{
      fieldName: draft.fieldName,
      fieldType: draft.fieldType,
      feature: draft.feature,
      prompt: draft.prompt,
      additionalPrompt: draft.additionalPrompt,
      fieldSchema: draft.fieldSchema,
      computeSchema: draft.computeSchema,
      externalKey: draft.externalKey
    }];
  }

  private buildEditRowFromPromptDraft(draftField: PromptTuningGenerateDraftField): EditField {
    const mappedFieldType = this.mapPromptDraftFieldType(draftField.fieldType);
    const normalizedFeature = this.normalizePromptDraftValue(draftField.feature);
    const normalizedPrompt = this.normalizePromptDraftValue(draftField.prompt);
    const normalizedAdditionalPrompt = this.normalizePromptDraftValue(draftField.additionalPrompt);
    const normalizedExternalKey = this.normalizePromptDraftValue(draftField.externalKey);
    const normalizedFieldSchema = this.normalizePromptDraftValue(draftField.fieldSchema);
    const normalizedComputeSchema = this.normalizePromptDraftValue(draftField.computeSchema);

    return {
      fieldName: this.normalizePromptDraftValue(draftField.fieldName),
      fieldType: mappedFieldType,
      feature: normalizedFeature,
      prompt: normalizedPrompt,
      additionalPrompt: normalizedAdditionalPrompt,
      fieldSchema: mappedFieldType === 'Complex' || mappedFieldType === 'Compute' ? normalizedFieldSchema : '',
      computeSchema: mappedFieldType === 'Complex' || mappedFieldType === 'Compute' ? normalizedComputeSchema : '',
      externalKey: normalizedExternalKey,
      modifiedAt: '',
      isEditing: true,
      isNew: true,
      originalFieldName: '',
      originalSchema: null,
      originalFeature: ''
    };
  }

  private consumePromptTuningGenerateDraft(): PromptTuningGenerateDraft | null {
    let rawDraft = '';
    try {
      rawDraft = sessionStorage.getItem(this.trainingGenerateDraftStorageKey) || '';
    } catch {
      return null;
    }

    if (!rawDraft) {
      return null;
    }

    let parsed: any;
    try {
      parsed = JSON.parse(rawDraft);
    } catch {
      sessionStorage.removeItem(this.trainingGenerateDraftStorageKey);
      return null;
    }

    if (!parsed || typeof parsed !== 'object') {
      sessionStorage.removeItem(this.trainingGenerateDraftStorageKey);
      return null;
    }

    const payload: PromptTuningGenerateDraft = {
      documentModel: String(parsed.documentModel || ''),
      createdAt: Number(parsed.createdAt || 0),
      returnUrl: String(parsed.returnUrl || ''),
      autoCreate: parsed.autoCreate === true,
      fields: Array.isArray(parsed.fields)
        ? parsed.fields.map((field: any) => ({
          fieldName: String(field?.fieldName || ''),
          fieldType: String(field?.fieldType || ''),
          feature: String(field?.feature || ''),
          prompt: String(field?.prompt || ''),
          additionalPrompt: String(field?.additionalPrompt || ''),
          fieldSchema: String(field?.fieldSchema || ''),
          computeSchema: String(field?.computeSchema || ''),
          externalKey: String(field?.externalKey || '')
        }))
        : undefined,
      fieldName: String(parsed.fieldName || ''),
      fieldType: String(parsed.fieldType || ''),
      feature: String(parsed.feature || ''),
      prompt: String(parsed.prompt || ''),
      additionalPrompt: String(parsed.additionalPrompt || ''),
      fieldSchema: String(parsed.fieldSchema || ''),
      computeSchema: String(parsed.computeSchema || ''),
      externalKey: String(parsed.externalKey || '')
    };

    const isExpired = payload.createdAt <= 0 || (Date.now() - payload.createdAt) > this.trainingGenerateDraftTtlMs;
    if (isExpired) {
      sessionStorage.removeItem(this.trainingGenerateDraftStorageKey);
      return null;
    }

    if (payload.documentModel !== this.documentModel) {
      return null;
    }

    sessionStorage.removeItem(this.trainingGenerateDraftStorageKey);
    return payload;
  }

  private mapPromptDraftFieldType(value: string): 'Simple' | 'Array' | 'Complex' | 'Compute' {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'normal' || normalized === 'simple') {
      return 'Simple';
    }

    if (normalized === 'array') {
      return 'Array';
    }

    if (normalized === 'complex') {
      return 'Complex';
    }

    if (normalized === 'compute') {
      return 'Compute';
    }

    return 'Simple';
  }

  private normalizePromptDraftValue(value: string): string {
    const normalized = String(value || '').trim();
    if (!normalized || normalized === '—') {
      return '';
    }

    return normalized;
  }

  get isPopupFieldNameDuplicate(): boolean {
    if (this.editPopupIndex < 0 || this.editPopupColumn !== 'fieldName') return false;
    const name = (this.fields[this.editPopupIndex]?.fieldName ?? '').trim().toLowerCase();
    if (!name) return false;
    return this.fields.some((f, idx) => idx !== this.editPopupIndex && f.fieldName.trim().toLowerCase() === name);
  }

  private getCurrentPopupValue(): string {
    if (this.editPopupIndex < 0 || !this.editPopupColumn) {
      return '';
    }
    return String((this.fields[this.editPopupIndex] as any)[this.editPopupColumn] ?? '');
  }

  get isPopupDoneDisabled(): boolean {
    if (this.editPopupIndex < 0 || !this.editPopupColumn) return true;

    const current = this.getCurrentPopupValue().trim();
    const original = String(this._popupOpenValue ?? '').trim();

    if (this.editPopupColumn === 'fieldSchema' || this.editPopupColumn === 'computeSchema') {
      const errorKey = `${this.editPopupColumn}_${this.editPopupIndex}`;
      const hasExistingError = !!this.jsonSchemaErrors[errorKey];
      if (current && hasExistingError) {
        return true;
      }
    }

    if (this.editPopupColumn === 'fieldName' && this.isPopupFieldNameDuplicate) {
      return true;
    }

    return current === original;
  }

  openCellPopup(index: number, column: string): void {
    // Non-admins can open the popup to view the value, but never enter edit mode
    if (this.canEdit && !this.fields[index].isEditing) {
      this.editField(index);
    }
    this.editPopupIndex = index;
    this.editPopupColumn = column;
    this._popupOpenValue = (this.fields[index] as any)[column];
    this.cdr.detectChanges();
  }

  openInlineEdit(index: number, column: string): void {
    if (!this.canEdit) return;
    if (!this.fields[index].isEditing) {
      this.editField(index);
    }
    this._inlineOpenValue = (this.fields[index] as any)[column];
    this.inlineEditIndex = index;
    this.inlineEditColumn = column;
    this.cdr.detectChanges();
  }

  closeInlineEdit(): void {
    this._inlineOpenValue = undefined;
    this.inlineEditIndex = -1;
    this.inlineEditColumn = '';
    this.cdr.detectChanges();
  }

  cancelInlineEdit(): void {
    if (this.inlineEditIndex >= 0) {
      (this.fields[this.inlineEditIndex] as any)[this.inlineEditColumn] = this._inlineOpenValue ?? '';
    }
    this._inlineOpenValue = undefined;
    this.inlineEditIndex = -1;
    this.inlineEditColumn = '';
    this.cdr.detectChanges();
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMousedown(event: MouseEvent): void {
    if (this.inlineEditIndex < 0) return;
    const target = event.target as HTMLElement;
    if (!target.closest('.cell-inline-select') && !target.closest('.cell-inline-input')) {
      this.cancelInlineEdit();
    }
  }

  closePopup(): void {
    if (this.editPopupIndex >= 0) {
      this.validateJsonField(this.editPopupIndex, 'fieldSchema');
      this.validateJsonField(this.editPopupIndex, 'computeSchema');
      if (
        this.hasJsonError(this.editPopupIndex, 'fieldSchema') ||
        this.hasJsonError(this.editPopupIndex, 'computeSchema')
      ) {
        this.cdr.detectChanges();
        return;
      }
    }
    this._popupOpenValue = undefined;
    this.editPopupIndex = -1;
    this.editPopupColumn = '';
    this.cdr.detectChanges();
  }

  cancelPopup(): void {
    if (this.editPopupIndex >= 0) {
      (this.fields[this.editPopupIndex] as any)[this.editPopupColumn] = this._popupOpenValue ?? '';
      if (this.editPopupColumn === 'fieldSchema' || this.editPopupColumn === 'computeSchema') {
        delete this.jsonSchemaErrors[`${this.editPopupColumn}_${this.editPopupIndex}`];
      }
    }
    this._popupOpenValue = undefined;
    this.editPopupIndex = -1;
    this.editPopupColumn = '';
    this.cdr.detectChanges();
  }

  getColumnLabel(column: string): string {
    const labels: Record<string, string> = {
      fieldName: 'Field Name',
      fieldType: 'Field Type',
      feature: 'Feature',
      prompt: 'Prompt',
      additionalPrompt: 'Additional Prompt',
      fieldSchema: 'Field Schema',
      computeSchema: 'Compute Schema',
      externalKey: 'External Key',
    };
    return labels[column] || column;
  }

  /** Undo — restores snapshot value for the current popup column */
  undoPopupCell(): void {
    if (this.editPopupIndex < 0) return;
    const snapshot = this.originalFieldSnapshots[this.editPopupIndex];
    if (!snapshot) return;
    (this.fields[this.editPopupIndex] as any)[this.editPopupColumn] =
      (snapshot as any)[this.editPopupColumn] ?? '';
    if (this.editPopupColumn === 'fieldSchema' || this.editPopupColumn === 'computeSchema') {
      delete this.jsonSchemaErrors[`${this.editPopupColumn}_${this.editPopupIndex}`];
    }
    this.cdr.detectChanges();
  }

  /** Clear All — empties the current popup column value */
  clearPopupCell(): void {
    if (this.editPopupIndex < 0) return;
    const field = this.fields[this.editPopupIndex];
    if (this.editPopupColumn === 'fieldName' && !field.isNew) return;
    (field as any)[this.editPopupColumn] = '';
    if (this.editPopupColumn === 'fieldSchema' || this.editPopupColumn === 'computeSchema') {
      delete this.jsonSchemaErrors[`${this.editPopupColumn}_${this.editPopupIndex}`];
    }
    this.cdr.detectChanges();
  }

  /** Copy All — copies current popup column value to clipboard */
  copyPopupCell(): void {
    if (this.editPopupIndex < 0) return;
    const val = String((this.fields[this.editPopupIndex] as any)[this.editPopupColumn] ?? '');
    if (!val.trim()) {
      this.notificationService.showError('Nothing to copy — field is empty');
      return;
    }
    navigator.clipboard.writeText(val)
      .then(() => this.notificationService.showSuccess('Copied to clipboard'))
      .catch(() => this.notificationService.showError('Failed to copy to clipboard'));
  }

  toggleJsonPreview(index: number, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.jsonPreviewOpen[index] = !this.jsonPreviewOpen[index];
    if (this.jsonPreviewOpen[index] && event) {
      const btn = (event.currentTarget as HTMLElement);
      const rect = btn.getBoundingClientRect();
      this.jsonPreviewPos[index] = {
        top: rect.bottom + 4,
        left: Math.min(rect.left, window.innerWidth - 510)
      };
    }
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.jsonPreviewOpen = {};
  }

  startJsonUpload(fileInput: HTMLInputElement, event: Event): void {
    event.stopPropagation();
    this._fileSelected = false;
    this.isJsonUploading = true;
    this.cdr.detectChanges();
    fileInput.click();

    // If user dismisses the file picker without selecting, reset the spinner
    const onWindowFocus = () => {
      setTimeout(() => {
        if (!this._fileSelected) {
          this.isJsonUploading = false;
          this.cdr.detectChanges();
        }
      }, 300);
    };
    window.addEventListener('focus', onWindowFocus, { once: true });
  }

  handleJsonUpload(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this._fileSelected = true;
      const file = input.files[0];

      if (!file.name.endsWith('.json')) {
        this.notificationService.showError('Please select a valid JSON file');
        input.value = '';
        this.isJsonUploading = false;
        this.cdr.detectChanges();
        return;
      }

      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        this.notificationService.showError('File size must be less than 5MB');
        input.value = '';
        this.isJsonUploading = false;
        this.cdr.detectChanges();
        return;
      }

      // Spinner is already showing from startJsonUpload()

      const stopUploading = () => {
        // Minimum 500 ms so spinner is always visible to the user
        setTimeout(() => {
          this.isJsonUploading = false;
          this.cdr.detectChanges();
        }, 500);
      };

      const reader = new FileReader();
      reader.onload = () => {
        try {
          const parsed = JSON.parse(reader.result as string);

          // Validate that JSON only contains allowed keys
          const allowedKeys = ['extraction schema', 'compute schema'];
          const parsedKeys = Object.keys(parsed);
          const invalidKeys = parsedKeys.filter(k => !allowedKeys.includes(k));
          if (invalidKeys.length > 0 || (!parsed['extraction schema'] && !parsed['compute schema'])) {
            this.notificationService.showError('Invalid JSON \u2014 only "extraction schema" and "compute schema" keys are allowed');
            input.value = '';
            stopUploading();
            return;
          }

          const field = this.fields[index];
          field.jsonFile = file;
          field.parsedJsonContent = parsed;
          const isComputeType = field.fieldType === 'Compute';

          if (parsed['extraction schema'] && !isComputeType) {
            field.fieldSchema = JSON.stringify(parsed['extraction schema'], null, 2);
          }
          if (parsed['compute schema']) {
            field.computeSchema = JSON.stringify(parsed['compute schema'], null, 2);
          }

          // Clear any previous errors
          delete this.jsonSchemaErrors[`fieldSchema_${index}`];
          delete this.jsonSchemaErrors[`computeSchema_${index}`];

          stopUploading();
        } catch (e) {
          this.notificationService.showError('Invalid JSON file. Please upload a valid JSON file.');
          input.value = '';
          stopUploading();
        }
      };
      reader.onerror = () => {
        this.notificationService.showError('Failed to read the file.');
        input.value = '';
        stopUploading();
      };
      reader.readAsText(file);
    }
  }

  deleteField(index: number): void {
    if (!this.canEdit) return;
    this.confirmAction = 'delete';
    this.confirmMessage = `Are you sure you want to delete field "${this.fields[index].fieldName}"?`;
    this.confirmIndex = index;
    this.showConfirmModal = true;
  }

  cancelConfirm(): void {
    this.showConfirmModal = false;
    this.confirmAction = '';
    this.confirmMessage = '';
    this.confirmIndex = -1;
    this.changeReason = '';
  }

  executeConfirm(): void {
    this.showConfirmModal = false;
    if (this.confirmAction === 'update' && this.confirmIndex >= 0) {
      const reason = this.changeReason.trim();
      this.executeUpdateField(this.confirmIndex, reason);
    } else if (this.confirmAction === 'create' && this.confirmIndex >= 0) {
      this.executeCreateField(this.confirmIndex);
    } else if (this.confirmAction === 'delete' && this.confirmIndex >= 0) {
      const field = this.fields[this.confirmIndex];
      if (field.isNew) {
        this.fields.splice(this.confirmIndex, 1);
        this.cdr.detectChanges();
      } else {
        this.documentModelService.deleteField(this.documentModel, field.fieldName).subscribe({
          next: () => {
            this.fields.splice(this.confirmIndex, 1);
            this.notificationService.showSuccess(`Field "${field.fieldName}" deleted successfully`);
            // Invalidate cache so Truth Management fetches fresh schema on next visit.
            this.truthManagementService.invalidateDocumentModelCache(this.documentModel);
            if (this.fields.length === 0) {
              this.router.navigate(['/admin-settings']);
            } else {
              this.loadFields();
            }
          },
          error: (error) => {
            const msg = error.error?.detail || error.error?.message || (typeof error.error === 'string' ? error.error : error.message || 'Unknown error');
            this.notificationService.showError(`Failed to delete field: ${msg}`);
          }
        });
      }
    }
    this.confirmAction = '';
    this.confirmIndex = -1;
  }



  togglePromptExpansion(index: number): void {
    this.promptExpanded[index] = !this.promptExpanded[index];
  }

  toggleAdditionalPromptExpansion(index: number): void {
    this.additionalPromptExpanded[index] = !this.additionalPromptExpanded[index];
  }

  toggleFieldSchemaExpansion(index: number): void {
    this.fieldSchemaExpanded[index] = !this.fieldSchemaExpanded[index];
  }

  toggleComputeSchemaExpansion(index: number): void {
    this.computeSchemaExpanded[index] = !this.computeSchemaExpanded[index];
  }

  toggleExternalKeyExpansion(index: number): void {
    this.externalKeyExpanded[index] = !this.externalKeyExpanded[index];
  }

  showSchemaTooltip(index: number, event: MouseEvent): void {
    this.hoveredSchemaIndex = index;
    this.schemaTooltipPos = this.calculateSchemaTooltipPosition(event);
  }

  hideSchemaTooltip(): void {
    this.hoveredSchemaIndex = -1;
  }

  showComputeSchemaTooltip(index: number, event: MouseEvent): void {
    this.hoveredComputeSchemaIndex = index;
    this.computeSchemaTooltipPos = this.calculateSchemaTooltipPosition(event);
  }

  hideComputeSchemaTooltip(): void {
    this.hoveredComputeSchemaIndex = -1;
  }

  private calculateSchemaTooltipPosition(event: MouseEvent): { top: number; left: number; up: boolean } {
    const anchor = event.currentTarget as HTMLElement;
    const rect = anchor.getBoundingClientRect();
    const tooltipHeight = 300;
    const offset = 6;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const up = spaceBelow < tooltipHeight && spaceAbove > spaceBelow;
    const top = up ? rect.top - offset : rect.bottom + offset;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - 520));
    return { top, left, up };
  }

  formatJsonForTooltip(jsonStr: string): string {
    if (!jsonStr || !jsonStr.trim()) return '';
    try {
      return JSON.stringify(JSON.parse(jsonStr), null, 2);
    } catch {
      return jsonStr;
    }
  }

  validateJsonField(index: number, fieldType: 'fieldSchema' | 'computeSchema'): void {
    const field = this.fields[index];
    const value = fieldType === 'fieldSchema' ? field.fieldSchema : field.computeSchema;
    const errorKey = `${fieldType}_${index}`;

    if (!value || value.trim() === '') {
      delete this.jsonSchemaErrors[errorKey];
      return;
    }

    try {
      JSON.parse(value);
      delete this.jsonSchemaErrors[errorKey];
    } catch (e) {
      this.jsonSchemaErrors[errorKey] = `Invalid JSON in ${fieldType === 'fieldSchema' ? 'Field Schema' : 'Compute Schema'}`;
    }
  }

  hasJsonError(index: number, fieldType: string): boolean {
    return !!this.jsonSchemaErrors[`${fieldType}_${index}`];
  }

  onPopupSchemaInputChange(fieldType: 'fieldSchema' | 'computeSchema'): void {
    if (this.editPopupIndex < 0) return;
    this.validateJsonField(this.editPopupIndex, fieldType);
    this.cdr.detectChanges();
  }

  cancel(): void {
    this.router.navigate(['/admin-settings']);
  }

  navigateToStatsDashboard(): void {
    this.router.navigate(['/stats-dashboard']);
  }

  navigateToEvaluationDashboard(): void {
    this.router.navigate(['/dashboard']);
  }

  navigateToOperationsRequestAnalytics(): void {
    this.clientCapability.navigateToOpsHealth();
  }

  logout(): void {
    this.authService.logout();
    this.router.navigate(['/login']);
  }
}
